import { ProviderTitleDropdownMap } from "../CMS/schema"
import { FileStatusDisplayIdEnum } from "../Files/schema"
import { LiabilityInsuranceType } from "../PracticeProviders/useProviderActivationDataExtended"
import { BillingAddress, PartialPractice } from "../Practices/schema"
import { LiabilityInsurance as PracticeLiabilityInsurance } from "../Practices/schema"

export type ProviderStatusReturn =
  | "pending"
  | "active"
  | "suspended"
  | "deactivated"
  | "pending-activation"
  | "orphaned"

// TODO: Replace other status types above when we start to query from /tableData.
export interface ProviderStatusAdjectivesMap extends Record<
  ProviderStatusReturn,
  string
> {}

export interface ProviderStatusVerbsMap extends Partial<
  Record<ProviderStatusReturn, string>
> {}

export enum AvailableDisplayId {
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
}

export type AvailableDaysOfOperation = boolean[]

export type ProviderStatusDisplayId =
  | "pending"
  | "active"
  | "suspended"
  | "deactivated"
  | "pending-activation"
  | "orphaned"
export enum ProviderStatusDisplayIdEnum {
  PENDING = "pending",
  ACTIVE = "active",
  SUSPENDED = "suspended",
  DEACTIVATED = "deactivated",
  PENDING_ACTIVATION = "pending-activation",
  ORPHANED = "orphaned",
}

export interface ProviderSearchResult {
  providerId: string
  name: string
}

export interface ProviderNameResult {
  id?: string
  userId: string
  firstName?: string
  lastName?: string
}

export type Title = keyof ProviderTitleDropdownMap

export interface InsuranceCarrier {
  displayId: string
  isHighlighted: boolean
}
export interface LiabilityInsurance {
  id?: string
  fileName?: string
  fileId?: string
  issueDate?: Date
  expirationDate?: Date
  createdBy?: string
  liabilityInsuranceStatusDisplayId: string
  fileStatusDisplayId?: FileStatusDisplayIdEnum
}

export interface ProviderBlockOutDate {
  id?: string
  startDate: string
  endDate: string
  reason?: string
}

export interface ProviderCredential {
  id?: string
  stateCode?: string
  expireDate?: Date
  credentialTypeDisplayId: string
  fileId?: string
  fileName?: string
  fileStatusDisplayId?: FileStatusDisplayIdEnum
  updatedDateTime?: string
  createdBy?: string
  createdDateTime?: string
  createdByFullName?: string
}

export interface ProviderLocation {
  id: string
  createdBy: string
}

export interface ProviderDisclosure {
  id?: string
  fileName?: string
  fileId?: string
  isSupportingDocument?: boolean
  fileStatusDisplayId?: string
  envelopeId?: string
  updatedDateTime?: string
  archivedDateTime?: string
  createdBy?: string
  createdDateTime?: string
  createdByFullName?: string
}

export type EmailTypeDisplayId = "primary" | "referral"

export interface ProviderEmail {
  id?: string
  address: string
  emailTypeDisplayId?: EmailTypeDisplayId
}

export interface ProviderLicense {
  id?: string
  licenseNumber?: string
  stateCode?: string
  expireDate?: Date
  fileName?: string
  fileId?: string
  licenseTypeDisplayId?: string
  licenseStatusDisplayId?: string
  fileStatusDisplayId?: string
  otherLicense?: string
}

export type ProviderLicenseStatus =
  | "pending"
  | "active"
  | "expiring-soon"
  | "expired"
  | "not-available"

export type PhoneTypeDisplayId = "mobile" | "office"

export type CommunitiesServed =
  | "aviation-professionals"
  | "bisexual-allied"
  | "blind-allied"
  | "body-positivity"
  | "cancer"
  | "deaf-allied"
  | "gay-allied"
  | "hiv-aids-allied"
  | "immuno-disorders"
  | "intersex-allied"
  | "lesbian-allied"
  | "little-person-allied"
  | "medical-professionals"
  | "non-binary-allied"
  | "open-relationships-non-monogamy"
  | "queer-allied"
  | "racial-justice-allied"
  | "sex-positive-kink-allied"
  | "sex-worker-allied"
  | "single-mother"
  | "transgender"
  | "veterans"

export type LanguageOptions =
  | "arabic"
  | "armenian"
  | "bengali"
  | "bosnian"
  | "bulgarian"
  | "cantonese"
  | "croatian"
  | "dutch"
  | "english"
  | "farsi"
  | "filipino"
  | "french"
  | "french-canadian"
  | "french-creole"
  | "german"
  | "greek"
  | "gujarati"
  | "haitian-creole"
  | "hebrew"
  | "hindi"
  | "hungarian"
  | "indonesian"
  | "italian"
  | "japanese"
  | "korean"
  | "laotian"
  | "lingala"
  | "malayalam"
  | "mandarin"
  | "norwegian"
  | "polish"
  | "portuguese"
  | "punjabi"
  | "romanian"
  | "russian"
  | "serbian"
  | "sinhalese"
  | "spanish"
  | "swedish"
  | "tagalog"
  | "tamil"
  | "thai"
  | "turkish"
  | "ukranian"
  | "urdu"
  | "vietnamese"
  | "yiddish"

export interface ProviderPhone {
  id?: string
  number?: string
  countryCode?: number
  phoneTypeDisplayId?: PhoneTypeDisplayId
}

export type ProviderTelehealthPlatformOptions =
  | "doxy-me"
  | "facetime"
  | "google-meet"
  | "jituzu"
  | "microsoft-teams"
  | "optum"
  | "other"
  | "psychology-today"
  | "simplepractice"
  | "teladoc"
  | "thera-link"
  | "theranest"
  | "theraplatform"
  | "therapyappointment"
  | "therapynotes"
  | "vsee"
  | "zoom"

export interface ProviderTelehealthPlatform {
  telehealthPlatformDisplayId: ProviderTelehealthPlatformOptions
  otherPlatformName?: string
}

export type ProviderTherapyTypeOptions =
  | "accelerated-experiential-dynamic-psychotherapy-aedp"
  | "acceptance-and-commitment-therapy"
  | "adlerian-therapy"
  | "animal-assisted-therapy"
  | "applied-behavior-analysis"
  | "art-therapy"
  | "attachment-based-therapy"
  | "behavioral-activation"
  | "bibliotherapy"
  | "biofeedback"
  | "brain-stimulation-therapy"
  | "christian-counseling"
  | "coaching"
  | "cognitive-behavioral-therapy"
  | "cognitive-processing-therapy"
  | "cognitive-stimulation-therapy"
  | "compassion-focused-therapy"
  | "culturally-sensitive-therapy"
  | "dance-therapy"
  | "dialectical-behavior-therapy"
  | "eclectic-therapy"
  | "emdr"
  | "emotionally-focused-therapy"
  | "equine-assisted-therapy"
  | "existential-therapy"
  | "experiential-therapy"
  | "exposure-and-response-prevention"
  | "expressive-arts-therapy"
  | "eye-movement-desensitization-and-reprocessing-therapy"
  | "family-systems-therapy"
  | "feminist-therapy"
  | "forensic-therapy"
  | "gestalt-therapy"
  | "human-givens-therapy"
  | "humanistic-therapy"
  | "hypnotherapy"
  | "imago-relationship-therapy"
  | "integrative-therapy"
  | "internal-family-systems-therapy"
  | "interpersonal-psychotherapy"
  | "jungian-therapy"
  | "marriage-and-family-therapy"
  | "mentalization-based-therapy"
  | "mindfulness-based-cognitive-therapy"
  | "motivational-interviewing"
  | "multicultural-therapy"
  | "music-therapy"
  | "narrative-therapy"
  | "neurofeedback"
  | "neuro-linguistic-programming-therapy"
  | "parent-child-interaction-therapy-pcit"
  | "person-centered-therapy"
  | "play-therapy"
  | "positive-psychology"
  | "prolonged-exposure-therapy"
  | "psychoanalytic-therapy"
  | "psychodynamic-therapy"
  | "psychological-testing-and-evaluation"
  | "rational-emotive-behavior-therapy"
  | "reality-therapy"
  | "relational-therapy"
  | "sandplay-therapy"
  | "schema-therapy"
  | "social-recovery-therapy"
  | "solution-focused-brief-therapy"
  | "somatic-therapy"
  | "strength-based-therapy"
  | "structural-family-therapy"
  | "the-gottman-method"
  | "therapeutic-intervention"
  | "transpersonal-therapy"
  | "trauma-focused-cognitive-behavior-therapy"

export type ProviderGender = "female" | "male" | "nonbinary"
export interface ProviderGenderMap extends Record<ProviderGender, string> {}

export type ProviderMilitaryStatus =
  | "active-reservist"
  | "combat-veteran"
  | "other"
  | "veteran"
export interface ProviderMilitaryStatusMap extends Record<
  ProviderMilitaryStatus,
  string
> {}

export type ProviderEthnicGroup =
  | "black"
  | "hispanic-and-latino"
  | "asian"
  | "native-american"
  | "mixed-race-family"
  | "middle-eastern"
  | "arab-american"
  | "caucasian"
  | "israeli"
export interface ProviderEthnicGroupMap extends Record<
  ProviderEthnicGroup,
  string
> {}

export type ProviderReligions =
  | "buddhist"
  | "christian"
  | "hindu"
  | "jewish"
  | "muslim"
  | "secular-and-nonreligious"
  | "sikh"
  | "lds"
export interface ProviderReligionsMap extends Record<
  ProviderReligions,
  string
> {}

export type ProviderSexualOrientation =
  | "bisexual"
  | "gay"
  | "heterosexual"
  | "other"
export interface ProviderSexualOrientationMap extends Record<
  ProviderSexualOrientation,
  string
> {}

export type SpecialtyType = "primary" | "additional"

export interface ProviderSpecialty {
  id?: string
  specialtyDisplayId: string
  specialtyTypeDisplayId: SpecialtyType
}

export interface ProviderEducationState {
  graduateSchoolName?: string
  graduateYear?: string
  graduateDegreeDisplayId?: string
}

export interface ProviderActivationData {
  id: string
  firstName: string
  lastName: string
  userId: string
  activationRequested: string
  liabilityInsurances: LiabilityInsurance[]
}

export interface ProviderActivationDataExtended extends ProviderActivationData {
  associatedPractice: string
  billingAddress: BillingAddress
  documentId: string
  insurances: LiabilityInsurance[] | PracticeLiabilityInsurance[]
  liabilityType: LiabilityInsuranceType
  liabilityPolicy: string
  licenses: ProviderLicense[]
  name: string
  practiceAffiliateId: string
  practiceId: string
  requestDate: Date
}

export interface ProviderOnboardingData {
  affiliatedPractice: PartialPractice | undefined
  provider: Provider | null
  userOnboarded: boolean
}

export interface ProviderAuthRelease {
  id: string
  envelopeId?: string
  fileName?: string
  fileId?: string
  fileStatusDisplayId?: string
  signedBy?: string
  createdBy: string
  createdDateTime: string
  updatedBy: string
  updatedDateTime: string
  archivedDateTime?: string
  archivedBy?: string
}

export interface ProviderPhoto {
  fileName: string
  fileId: string
  createdBy: string
  createdByFullName: string
  createdDateTime: string
  fileStatusDisplayId: string
  thumbnailFileId?: string
  thumbnailBase64?: string
}

export interface Provider {
  id: string
  userId: string
  legacyAffiliateId?: number
  isPublicViewable: boolean
  titleDisplayId?: Title
  firstName?: string
  lastName?: string
  photoFileName?: string
  biography?: string
  practicingSince?: string
  gender?: string
  isTransgender?: boolean
  hasLicense: boolean
  isAcceptingNewPatients: boolean
  hasPatientWaitList: boolean
  isAvailableEveningHours: boolean
  isLanguageAttested: boolean
  isUnderSupervision: boolean
  graduateSchoolName?: string
  graduateYear?: string
  isInsurancePaneled: boolean
  photoFileId?: string
  activationRequested?: string
  providerPhoto?: ProviderPhoto
  // Enum values (displayId)
  providerStatusDisplayId: ProviderStatusDisplayId
  photoFileStatusDisplayId?: string
  militaryStatusDisplayId?: string
  faithOrientationDisplayId?: string
  graduateDegreeDisplayId?: string
  providerTypeDisplayId: string
  // List of enum (multiple selections, but still enum values)
  providerAgesSeenDisplayIds: string[]
  providerAvailableDisplayIds: AvailableDisplayId[]
  providerClientEthnicityDisplayIds: string[]
  providerCommunityDisplayIds: string[]
  providerDesignationDisplayIds: string[]
  providerEthnicGroupDisplayIds: string[]
  providerInsuranceDisplayIds: string[]
  providerLanguageDisplayIds: string[]
  providerParticipantDisplayIds: string[]
  providerReligionDisplayIds: string[]
  providerSessionTypeDisplayIds: string[]
  providerSexualOrientationDisplayIds: string[]
  providerTagDisplayIds: string[]
  providerTelehealthPlatforms: ProviderTelehealthPlatform[]
  providerTherapyTypeDisplayIds: ProviderTherapyTypeOptions[]
  // External Foreign Keys
  providerLocations: ProviderLocation[]
  // One to Many
  liabilityInsurances: LiabilityInsurance[]
  providerBlockOutDate: ProviderBlockOutDate[]
  providerCredentials: ProviderCredential[]
  providerDisclosures: ProviderDisclosure[]
  providerEmails: ProviderEmail[]
  providerLicenses: ProviderLicense[]
  providerPhones: ProviderPhone[]
  providerSpecialties: ProviderSpecialty[]
  providerAuthReleases: ProviderAuthRelease[]
  isVirtualAttested?: boolean | null
  updatedDateTime?: string
  updatedByUserId?: string
  createdDateTime: string
  createdByUserId: string
  deactivationReasonId?: number
  npiNumber: string | null
}

export interface ManagedProvider {
  firstName?: string
  lastName?: string
  isPublicViewable: boolean
  hasLicense: boolean
  isAcceptingNewPatients: boolean
  hasPatientWaitList: boolean
  isAvailableEveningHours: boolean
  isLanguageAttested: boolean
  isUnderSupervision: boolean
  isInsurancePaneled: boolean
  // Enum values (displayId)
  providerStatusDisplayId: ProviderStatusDisplayId
  providerTypeDisplayId: string
  // List of enum (multiple selections, but still enum values)
  providerAgesSeenDisplayIds: string[]
  providerAvailableDisplayIds: AvailableDisplayId[]
  providerClientEthnicityDisplayIds: string[]
  providerCommunityDisplayIds: string[]
  providerDesignationDisplayIds: string[]
  providerEthnicGroupDisplayIds: string[]
  providerInsuranceDisplayIds: string[]
  providerLanguageDisplayIds: string[]
  providerParticipantDisplayIds: string[]
  providerReligionDisplayIds: string[]
  providerSessionTypeDisplayIds: string[]
  providerSexualOrientationDisplayIds: string[]
  providerTagDisplayIds: string[]
  providerTelehealthPlatforms: ProviderTelehealthPlatform[]
  providerTherapyTypeDisplayIds: ProviderTherapyTypeOptions[]
  // External Foreign Keys
  providerLocations: ProviderLocation[]
  // One to Many
  liabilityInsurances: LiabilityInsurance[]
  providerBlockOutDate: ProviderBlockOutDate[]
  providerCredentials: ProviderCredential[]
  providerDisclosures: ProviderDisclosure[]
  providerEmails: ProviderEmail[]
  providerLicenses: ProviderLicense[]
  providerPhones: ProviderPhone[]
  providerSpecialties: ProviderSpecialty[]
  providerAuthReleases: ProviderAuthRelease[]
}

export interface ManagedProviderResponse {
  pendingActivationProviders: string[]
  activeProviders: string[]
  errorProviders: Map<string, string>
}
